import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Leaf, Sparkles, MessageCircle, Map, BookOpen, ArrowRight, Heart, Compass, Sun } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import "../styles/emergence.css";

export default function EmergenceLanding() {
  const { t, i18n } = useTranslation();
  const isFrench = i18n.language === 'fr';

  const features = [
    {
      icon: <Compass className="w-8 h-8" />,
      title: isFrench ? "Clarté intérieure" : "Inner Clarity",
      description: isFrench 
        ? "Un diagnostic doux pour comprendre où tu en es dans ton changement"
        : "A gentle diagnostic to understand where you are in your change journey"
    },
    {
      icon: <Map className="w-8 h-8" />,
      title: isFrench ? "Chemin personnel" : "Personal Path",
      description: isFrench
        ? "Un parcours 100% personnalisé, adapté à ton rythme"
        : "A 100% personalized journey, adapted to your rhythm"
    },
    {
      icon: <MessageCircle className="w-8 h-8" />,
      title: isFrench ? "Coach intérieur" : "Inner Coach",
      description: isFrench
        ? "Un accompagnement bienveillant, disponible quand tu en as besoin"
        : "A caring companion, available when you need it"
    },
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: isFrench ? "Journal intelligent" : "Smart Journal",
      description: isFrench
        ? "Capture tes pensées, découvre tes patterns, célèbre tes progrès"
        : "Capture your thoughts, discover your patterns, celebrate your progress"
    }
  ];

  const transitions = [
    { icon: "💼", label: isFrench ? "Reconversion professionnelle" : "Career change" },
    { icon: "🔥", label: isFrench ? "Burn-out & fatigue" : "Burnout & fatigue" },
    { icon: "💔", label: isFrench ? "Séparation & deuil" : "Separation & grief" },
    { icon: "🌟", label: isFrench ? "Nouveau projet de vie" : "New life project" },
    { icon: "💪", label: isFrench ? "Confiance en soi" : "Self-confidence" },
    { icon: "🧭", label: isFrench ? "Perte de sens" : "Loss of meaning" }
  ];

  return (
    <div className="emergence-bg min-h-screen">
      {/* Hero Section */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#3B7A8C]/5 via-transparent to-[#D4A574]/5" />
        
        <nav className="emergence-glass-header fixed top-0 left-0 right-0 z-50 px-6 py-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-[#2F4F4F]" />
              <span className="font-heading text-xl font-semibold text-[#2F4F4F]">Épanoui</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/emergence/parcours" className="text-sm text-[#7A8B99] hover:text-[#3B7A8C] transition-colors">
                {isFrench ? "Parcours 21j" : "21-Day Journeys"}
              </Link>
              <Link to="/emergence/chat" className="text-sm text-[#7A8B99] hover:text-[#3B7A8C] transition-colors">
                {isFrench ? "Coach IA" : "AI Coach"}
              </Link>
              <Link to="/emergence/diagnostic">
                <Button className="emergence-btn-primary text-sm px-6 py-2">
                  {isFrench ? "Diagnostic" : "Diagnostic"}
                </Button>
              </Link>
            </div>
          </div>
        </nav>

        <div className="pt-32 pb-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="emergence-badge inline-flex items-center gap-2 mb-6">
              <Leaf className="w-4 h-4" />
              {isFrench ? "Accompagnement au changement" : "Change companion"}
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2D5A66] mb-6 leading-tight">
              {isFrench ? (
                <>Traverse ton changement<br />avec <span className="text-[#3B7A8C]">sérénité</span></>
              ) : (
                <>Navigate your change<br />with <span className="text-[#3B7A8C]">serenity</span></>
              )}
            </h1>
            
            <p className="text-lg text-[#7A8B99] max-w-2xl mx-auto mb-10 leading-relaxed">
              {isFrench 
                ? "Reconversion, burn-out, séparation, nouveau départ... Tu traverses une période de transition ? Épanoui t'accompagne pas à pas, avec douceur et bienveillance."
                : "Career change, burnout, separation, new beginning... Going through a transition? Épanoui guides you step by step, with gentleness and care."}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/emergence/diagnostic" data-testid="start-diagnostic-btn">
                <Button className="emergence-btn-accent text-base px-8 py-4">
                  {isFrench ? "Démarrer mon diagnostic" : "Start my diagnostic"}
                  <Sparkles className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/emergence/chat" data-testid="talk-to-coach-btn">
                <Button variant="outline" className="rounded-full px-8 py-4 border-2 border-[#3B7A8C] text-[#3B7A8C] hover:bg-[#3B7A8C]/5">
                  {isFrench ? "Parler à mon coach" : "Talk to my coach"}
                  <MessageCircle className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Types de transitions */}
      <section className="py-16 px-6 bg-white/50">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-2xl font-semibold text-[#2D5A66] text-center mb-8">
            {isFrench ? "Tu traverses peut-être..." : "You might be going through..."}
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {transitions.map((item, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-[#3B7A8C]/10 text-[#3D4F5F] hover:border-[#3B7A8C]/30 transition-colors"
              >
                <span className="text-xl">{item.icon}</span>
                <span className="text-sm font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl font-bold text-[#2D5A66] mb-4">
              {isFrench ? "Un accompagnement complet" : "Complete support"}
            </h2>
            <p className="text-[#7A8B99] max-w-xl mx-auto">
              {isFrench 
                ? "Plusieurs agents IA travaillent ensemble pour t'accompagner à chaque étape de ton changement."
                : "Multiple AI agents work together to support you at every step of your change."}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, idx) => (
              <Card key={idx} className="emergence-card border-0 overflow-hidden">
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#3B7A8C]/10 to-[#8FB8A8]/10 flex items-center justify-center text-[#3B7A8C] mb-5">
                    {feature.icon}
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-[#2D5A66] mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-[#7A8B99] leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#3B7A8C] to-[#2D5A66]">
        <div className="max-w-4xl mx-auto text-center">
          <Heart className="w-12 h-12 text-[#D4A574] mx-auto mb-6" />
          <h2 className="font-heading text-3xl font-bold text-white mb-6">
            {isFrench ? "Notre philosophie" : "Our philosophy"}
          </h2>
          <p className="text-xl text-white/80 leading-relaxed mb-8">
            {isFrench 
              ? '"Tu n\'es pas bloqué(e). Tu es en transition. Et chaque transition mérite d\'être accompagnée avec douceur."'
              : '"You\'re not stuck. You\'re in transition. And every transition deserves gentle support."'}
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-white/70">
            <div className="flex items-center gap-2">
              <Sun className="w-5 h-5 text-[#D4A574]" />
              <span>{isFrench ? "Zéro pression" : "Zero pressure"}</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-[#D4A574]" />
              <span>{isFrench ? "Bienveillance" : "Kindness"}</span>
            </div>
            <div className="flex items-center gap-2">
              <Leaf className="w-5 h-5 text-[#D4A574]" />
              <span>{isFrench ? "À ton rythme" : "At your pace"}</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl font-bold text-[#2D5A66] mb-6">
            {isFrench ? "Prêt(e) à commencer ?" : "Ready to begin?"}
          </h2>
          <p className="text-[#7A8B99] mb-8">
            {isFrench 
              ? "Le diagnostic prend 5 minutes et t'offre une première vision claire de ta situation."
              : "The diagnostic takes 5 minutes and gives you a clear first view of your situation."}
          </p>
          <Link to="/emergence/diagnostic">
            <Button className="emergence-btn-primary text-lg px-10 py-5">
              {isFrench ? "Commencer gratuitement" : "Start for free"}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#3B7A8C]/10">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Leaf className="w-5 h-5 text-[#3B7A8C]" />
            <span className="font-heading text-[#2D5A66]">Épanoui</span>
          </div>
          <p className="text-sm text-[#7A8B99]">
            © 2025 Épanoui - {isFrench ? "Accompagnement au changement" : "Change companion"}
          </p>
        </div>
      </footer>
    </div>
  );
}
